import { AloftDate, type MLSListing, isDefined, isUndefined } from '@aloft/toolkit-shared';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useRecommendedProperties } from '../components/RecommendedCompSelection/hooks/useRecommendedProperties.ts';
import { useSubjectCraftsmanPropertyToCompSelectionProperty } from '../components/RecommendedCompSelection/hooks/useSubjectCraftsmanPropertyToCompSelectionProperty.ts';
import { CompSelectionState, schCompSearch } from '../components/RecommendedCompSelection/types.ts';
import { selectTopRentalRecommendationsFromListings } from '../components/RecommendedCompSelection/utils/buildRentalRecommendationLists.ts';
import { getDefaultRecommendedCompSearch } from '../components/RecommendedCompSelection/utils/getDefaultRecommendedCompSearch.ts';
import { handleAddClickForCompCreation } from '../components/RecommendedCompSelection/utils/handleAddClickForCompCreation.ts';
import { useUpdateOrderFeatures } from '../modules/orderManagement/components/OrderTable/hooks/useUpdateOrderFeatures.ts';
import { addComps } from '../redux/features/actions/addComps.ts';
import {
  selectEffectiveDate,
  selectOrder,
  selectRentalCompSequence,
} from '../redux/features/selectors/orderSelectors.ts';
import { selectSubjectPropertyDataEntity } from '../redux/features/selectors/selectSubjectPropertyDataEntity.ts';
import { useAppDispatch, useAppSelector } from '../redux/hooks.ts';
import {
  LocationType,
  formatProximityRequirementTooltip,
  getLocationTypeProximityRequirementMiles,
} from '../utils/locationTypeProximityRequirementMiles.ts';
import { proximityMiles } from '../utils/proximityMiles.ts';
import { useAutofillProperty } from './useAutofillProperty.ts';
import { useEdgeTrigger } from './useEdgeTrigger.ts';
import { useToaster } from './useToaster/useToaster.tsx';

const RENTAL_AUTO_SELECT_LIMIT = 3;

const buildRowSelectionForAutoAdd = (
  listings: MLSListing[],
  isInspectifyOrder: boolean,
  hasArvOption: boolean,
): CompSelectionState =>
  listings.reduce<CompSelectionState>((acc, listing) => {
    const id = listing.ToolkitListingID ?? listing.ListingId;
    if (!id) return acc;
    return {
      ...acc,
      [id]: {
        listingData: listing,
        ...(isInspectifyOrder && hasArvOption ? { arv: true } : {}),
      },
    };
  }, {});

export const useRentalComparablesInitialRecommendations = (): {
  showRentalComparablesInitialLoad: boolean;
} => {
  const { orderId } = useParams();
  const dispatch = useAppDispatch();
  const [isAutoApplyingComps, setIsAutoApplyingComps] = useState(false);
  const subject = useAppSelector(selectSubjectPropertyDataEntity);
  const effectiveDateJson = useAppSelector(selectEffectiveDate);
  const rentalCompSequence = useAppSelector(selectRentalCompSequence);
  const firstCompSaveBatch = useAppSelector((s) => s.order.compSaveQueue[0]);
  const order = useAppSelector(selectOrder);
  const { hasArvOption, isInspectifyOrder } = order;

  const rentalCompSequenceRef = useRef(rentalCompSequence);
  rentalCompSequenceRef.current = rentalCompSequence;

  const autoApplyDoneRef = useRef(false);
  const orderIdRef = useRef(orderId);
  orderIdRef.current = orderId;

  useEffect(() => {
    autoApplyDoneRef.current = false;
  }, [orderId]);

  const updateOrderFeatures = useUpdateOrderFeatures();
  const autofillProperty = useAutofillProperty();
  const makeToast = useToaster();

  const effectiveDateAloft = isDefined(effectiveDateJson)
    ? AloftDate.schema.parse(effectiveDateJson)
    : undefined;

  const { isIdle, isLoading, data, getProperties } = useRecommendedProperties({
    orderID: orderId ?? '',
    compType: 'rental',
  });

  const subjectComp = useMemo(() => {
    if (!subject || !effectiveDateAloft) return null;
    return useSubjectCraftsmanPropertyToCompSelectionProperty(subject, effectiveDateAloft);
  }, [subject, effectiveDateAloft]);

  const isSubjectGlaDefined = subject ? isDefined(subject.editableData.improvements.gla) : false;
  const isSubjectLotSizeDefined = subject ? isDefined(subject.editableData.site.area) : false;
  const isSubjectLocationDefined =
    subjectComp !== null && isDefined(subjectComp.latitude) && isDefined(subjectComp.longitude);
  const isRunnable =
    subject !== null &&
    effectiveDateAloft !== undefined &&
    isSubjectGlaDefined &&
    isSubjectLotSizeDefined &&
    isSubjectLocationDefined;

  useEdgeTrigger({
    condition:
      Boolean(orderId) &&
      isRunnable &&
      !isLoading &&
      isIdle &&
      isUndefined(data) &&
      subject !== null &&
      effectiveDateAloft !== undefined,
    action: () => {
      getProperties({
        search: schCompSearch.parse(getDefaultRecommendedCompSearch(subject!, effectiveDateAloft!)),
      });
    },
  });

  useEffect(() => {
    const oid = orderIdRef.current;
    if (!oid || !subject || !effectiveDateAloft || !subjectComp) return;
    if (rentalCompSequenceRef.current.length > 0) return;
    if (autoApplyDoneRef.current) return;
    if (!data || data.source !== 'abode') return;
    if (!('status' in data.body) || data.body.status !== 'found') return;
    if (!('output' in data.body) || !data.body.output?.results?.length) return;

    autoApplyDoneRef.current = true;

    const { output } = data.body;
    const { results } = output;

    setIsAutoApplyingComps(true);
    (async () => {
      try {
        if (rentalCompSequenceRef.current.length > 0) return;

        const topListings = selectTopRentalRecommendationsFromListings(
          results,
          subjectComp,
          [],
          hasArvOption,
          RENTAL_AUTO_SELECT_LIMIT,
        );
        if (topListings.length === 0) return;

        const locationType = subject.editableData.propertyIdentification.locationType as
          | LocationType
          | undefined;
        const proximityRequirementMiles = getLocationTypeProximityRequirementMiles(locationType);
        const subjectCoordinates = subject.editableData.propertyIdentification.coordinates;

        if (locationType && proximityRequirementMiles !== undefined && subjectCoordinates) {
          const outOfRangeCount = topListings.filter((listing) => {
            if (listing.Latitude === undefined || listing.Longitude === undefined) return false;
            const miles = proximityMiles(subjectCoordinates, {
              latitude: listing.Latitude,
              longitude: listing.Longitude,
            });
            return miles !== undefined && miles > proximityRequirementMiles;
          }).length;

          if (outOfRangeCount > 0) {
            makeToast({
              id: `proximity-requirement-exceeded-rental-auto-${oid}`,
              type: 'warning',
              title: 'Proximity warning',
              body: formatProximityRequirementTooltip(locationType, proximityRequirementMiles),
            });
          }
        }

        const rowSelection = buildRowSelectionForAutoAdd(
          topListings,
          isInspectifyOrder,
          hasArvOption,
        );

        await handleAddClickForCompCreation({
          isLoading: false,
          rowSelection,
          createComps: (comps) => {
            dispatch(addComps({ comps, sequenceTarget: 'rentalCompSequence' }));
            updateOrderFeatures(oid, comps).catch(() => undefined);
          },
          autofillProperty,
          effectiveDate: effectiveDateAloft.toJSON(),
          orderID: oid,
          isInspectifyOrder,
          hasArvOption,
          listingType: 'rentalListing',
        });
      } finally {
        setIsAutoApplyingComps(false);
      }
    })().catch(() => undefined);
  }, [
    autofillProperty,
    data,
    dispatch,
    effectiveDateAloft,
    hasArvOption,
    isInspectifyOrder,
    makeToast,
    subject,
    subjectComp,
    updateOrderFeatures,
  ]);

  const isPersistingQueuedRentalComps = firstCompSaveBatch?.sequenceTarget === 'rentalCompSequence';

  const showRentalComparablesInitialLoad =
    rentalCompSequence.length === 0 &&
    isRunnable &&
    (isLoading || isAutoApplyingComps || isPersistingQueuedRentalComps);

  return { showRentalComparablesInitialLoad };
};
